import { Link } from "react-router-dom";
import {
  ArrowRight, TrendingUp, Wallet, Clock, MapPin, Award, BookOpen, Shield, Zap, Sparkles,
} from "lucide-react";

const DIFFICULTY_MAP = {
  Bassa: { pct: 25, color: "#22C55E", label: "Bassa" },
  Media: { pct: 50, color: "#EAB308", label: "Media" },
  Alta: { pct: 75, color: "#F97316", label: "Alta" },
  Critica: { pct: 95, color: "#EF4444", label: "Critica" },
};

const SectionHeader = ({ n, icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-4 mb-6">
    <div
      className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
      style={{ background: "rgba(0,196,140,0.14)", border: "0.5px solid rgba(0,196,140,0.25)" }}
    >
      <Icon size={18} className="text-lovac-accent" />
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-3">
        <span className="font-mono text-[11px] uppercase tracking-wider text-lovac-muted">
          {n}
        </span>
        <span className="h-px flex-1 bg-lovac-border" />
      </div>
      <h3 className="font-display text-2xl sm:text-3xl mt-2 text-lovac-dark leading-tight">
        {title}
      </h3>
      {subtitle && (
        <p className="text-sm text-lovac-muted mt-2">{subtitle}</p>
      )}
    </div>
  </div>
);

const Gauge = ({ score }) => {
  const cfg = DIFFICULTY_MAP[score] || DIFFICULTY_MAP.Media;
  return (
    <div className="flex items-center gap-6">
      <div className="relative h-28 w-28 shrink-0">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="9" />
          <circle
            cx="50" cy="50" r="42" fill="none" stroke={cfg.color} strokeWidth="9" strokeLinecap="round"
            strokeDasharray={`${(cfg.pct / 100) * 263.8939} 263.8939`}
            style={{ transition: "stroke-dasharray 1.2s cubic-bezier(0.4, 0, 0.2, 1)" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-2xl text-lovac-dark leading-none">{cfg.pct}</span>
          <span className="text-[10px] uppercase tracking-wider text-lovac-muted mt-1">score</span>
        </div>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-lovac-muted">Difficulty</p>
        <p
          className="font-display text-3xl sm:text-4xl mt-1 leading-none"
          style={{ color: cfg.color }}
        >
          {cfg.label}
        </p>
      </div>
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-lovac-muted">{label}</p>
    <p className="font-display text-3xl sm:text-4xl text-lovac-dark mt-2 leading-tight">{value}</p>
    {sub && <p className="text-xs text-lovac-muted mt-2">{sub}</p>}
  </div>
);

export default function HiringScanReport({ scan, report, fee, onRestart }) {
  const cfg = DIFFICULTY_MAP[report.difficulty_score] || DIFFICULTY_MAP.Media;

  return (
    <>
      {/* HERO */}
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 50% at 50% 0%, rgba(0,196,140,0.10) 0%, rgba(0,196,140,0) 60%)",
          }}
        />
        <div className="container-lovac px-6 pt-20 pb-16 relative z-10">
          <span className="badge-on-dark">Hiring Scan · Report</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 text-on-dark leading-[1.05] max-w-4xl">
            <span className="text-lovac-accent">{scan.ruolo}</span>
            <br />
            <span className="text-on-dark-muted">
              {scan.seniority} · {scan.settore} · {scan.area}
            </span>
          </h1>
          <p className="text-on-dark-muted text-base sm:text-lg mt-6 max-w-2xl">
            Report di mercato elaborato da Lovac su benchmark italiano 2024–2025.
          </p>
        </div>
      </section>

      {/* SEZIONE 1, DIFFICULTY */}
      <section className="section bg-lovac" data-testid="report-section-1">
        <div className="container-lovac">
          <SectionHeader
            n="01 / 07"
            icon={TrendingUp}
            title="Quanto è difficile trovare questa figura"
          />
          <div className="card-lovac grid lg:grid-cols-[auto_1fr] gap-10 items-center">
            <Gauge score={report.difficulty_score} />
            <div className="lg:border-l lg:border-lovac lg:pl-10">
              <p className="text-base sm:text-lg text-lovac-dark leading-relaxed">
                {report.difficulty_explain}
              </p>
              <div className="mt-6 flex items-center gap-2 text-xs text-lovac-muted">
                <Shield size={14} className="text-lovac-accent" />
                <span>Score calcolato su disponibilità di mercato, competizione e RAL richiesta</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEZIONE 2, DISPONIBILITÀ */}
      <section className="section bg-lovac-2" data-testid="report-section-2">
        <div className="container-lovac">
          <SectionHeader
            n="02 / 07"
            icon={Award}
            title={`Profili disponibili in ${report.area_normalized || scan.area}`}
          />
          <div className="card-lovac">
            <Stat
              label="Bacino stimato"
              value={report.available_profiles}
              sub="Inclusi profili attivi (in cerca) e passivi (occupati ma intercettabili)"
            />
            <p className="text-xs text-lovac-muted mt-6 italic">
              Con il database proprietario Lovac di oltre 10 milioni di figure professionali verificate, accediamo anche al 70% passivo che non risponde agli annunci.
            </p>
          </div>
        </div>
      </section>

      {/* SEZIONE 3, RAL */}
      <section className="section bg-lovac" data-testid="report-section-3">
        <div className="container-lovac">
          <SectionHeader
            n="03 / 07"
            icon={Wallet}
            title="Range retributivo di mercato"
          />
          <div className="card-lovac">
            <Stat
              label="RAL lorda annua"
              value={report.avg_ral}
              sub="Range conservativo per il profilo richiesto sul mercato italiano"
            />
          </div>
        </div>
      </section>

      {/* SEZIONE 4, TIME TO HIRE */}
      <section className="section bg-lovac-2" data-testid="report-section-4">
        <div className="container-lovac">
          <SectionHeader
            n="04 / 07"
            icon={Clock}
            title="Time to hire, mercato vs Lovac"
          />
          <div className="grid md:grid-cols-2 gap-5">
            <div className="card-lovac">
              <p className="text-[11px] uppercase tracking-wider text-lovac-muted">Mercato standard</p>
              <p className="font-display text-3xl sm:text-4xl text-lovac-dark mt-2 leading-tight">
                {report.time_to_hire_market}
              </p>
              <p className="text-xs text-lovac-muted mt-3">
                Tempo medio con agenzia tradizionale o ricerca interna
              </p>
            </div>
            <div
              className="card-dark relative overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(0,196,140,0.10) 0%, transparent 60%)" }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Zap size={14} className="text-lovac-accent" />
                <p className="text-[11px] uppercase tracking-wider text-on-dark-muted">Con Lovac</p>
              </div>
              <p className="font-display text-3xl sm:text-4xl text-lovac-accent mt-2 leading-tight">
                {report.time_to_hire_lovac}
              </p>
              <p className="text-xs text-on-dark-muted mt-3">
                Grazie al database proprietario di 10M+ profili e hunting attivo
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEZIONE 5, DOVE SI TROVANO */}
      <section className="section bg-lovac" data-testid="report-section-5">
        <div className="container-lovac">
          <SectionHeader
            n="05 / 07"
            icon={MapPin}
            title="Dove si trovano questi profili"
          />
          <div className="grid md:grid-cols-2 gap-4">
            {(report.where_to_find || []).map((w, i) => (
              <div key={i} className="card-lovac flex items-start gap-4">
                <div
                  className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 font-mono text-xs text-lovac-accent"
                  style={{ background: "rgba(0,196,140,0.14)", border: "0.5px solid rgba(0,196,140,0.25)" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="text-base text-lovac-dark leading-relaxed">{w}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEZIONE 6, RIEPILOGO ANALISI LOVAC */}
      {report.summary && (
        <section className="section bg-lovac-2" data-testid="report-section-6">
          <div className="container-lovac">
            <SectionHeader
              n="06 / 07"
              icon={Sparkles}
              title="Riepilogo Analisi Lovac"
            />
            <div className="card-lovac">
              <p className="text-base sm:text-lg text-lovac-dark leading-relaxed">
                {report.summary}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* SEZIONE 7, FEE LOVAC */}
      <section className="section bg-lovac-dark text-on-dark grain relative overflow-hidden" data-testid="report-section-7">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(50% 60% at 50% 50%, rgba(0,196,140,0.12) 0%, rgba(0,196,140,0) 60%)",
          }}
        />
        <div className="container-lovac relative z-10">
          <div className="mb-6">
            <span className="badge-on-dark">07 / 07</span>
          </div>
          <h3 className="font-display text-3xl sm:text-4xl text-on-dark leading-tight max-w-3xl">
            La fee Lovac per questa ricerca.
          </h3>

          <div
            className="mt-10 rounded-2xl p-8 sm:p-10"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "0.5px solid rgba(0,196,140,0.25)",
              backdropFilter: "blur(8px)",
            }}
          >
            <p className="text-[11px] uppercase tracking-wider text-on-dark-muted">Success fee</p>
            <p className="font-display text-5xl sm:text-6xl text-lovac-accent mt-3 leading-none">
              15% della RAL
            </p>
            <p className="text-on-dark-muted mt-5 text-base leading-relaxed max-w-2xl">
              {fee?.notes || report.fee_notes || "La fee Lovac è il 15% della RAL della figura inserita, paghi solo a risultato ottenuto."}
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4 text-sm">
              {[
                "Paghi solo se inseriamo",
                "Garanzia periodo di prova",
                "Rimborso totale acconto",
              ].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-lovac-accent" />
                  <span className="text-on-dark">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FONTI */}
      {report.sources && report.sources.length > 0 && (
        <section className="py-16 bg-lovac-2">
          <div className="container-lovac">
            <div className="flex items-start gap-4 max-w-4xl">
              <div
                className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "rgba(0,196,140,0.10)" }}
              >
                <BookOpen size={16} className="text-lovac-accent" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-lovac-muted">Metodologia</p>
                <p className="text-sm text-lovac-dark mt-2 leading-relaxed">
                  Stime elaborate sui seguenti benchmark di mercato italiano 2024–2025:
                </p>
                <ul className="mt-4 space-y-2">
                  {report.sources.map((s, i) => (
                    <li key={i} className="text-sm text-lovac-muted flex items-start gap-2">
                      <span className="text-lovac-accent mt-1.5">·</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-lovac-muted mt-6 italic leading-relaxed">
                  I dati sono stime conservative su benchmark pubblici. Per dati live sul nostro database di 10M+ profili, prenota una call con un recruiter Lovac.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-narrow text-center relative z-10">
          <span className="badge-on-dark">Prossimo passo</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-6 text-on-dark leading-tight">
            Vuoi che ce ne occupiamo noi?
          </h2>
          <p className="text-on-dark-muted mt-5 text-base max-w-xl mx-auto leading-relaxed">
            Un recruiter Lovac ti contatterà entro 24 ore. Nel frattempo, prenota una call di 30 minuti.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            <Link to="/contatti" className="btn-primary">
              Prenota una call <ArrowRight size={16} />
            </Link>
            <button onClick={onRestart} className="btn-outline-dark">
              Genera un altro report
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
